/*
  This is used to delete a template section.
*/

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import CustomDialog from 'components/CustomDialog/customDialog';
import CustomButton from 'components/Button/CustomButton';
import { setDialog } from 'store/actions/dialogActions';
import selectors from 'store/selectors';
import DIALOGS from 'utils/dialogIds';

import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import { DialogFooter, DialogHeader } from 'common/styles/main';
import CloseIcon from 'components/Icons/closeIcon';
import SectionIcon from 'components/Icons/sectionIcon';
import { DialogContent } from './styles';

// This is used to delete a template section.
const DeleteTemplateSection = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectors.getDialogState);
  const { open, data } = dialogState?.[DIALOGS?.DELETE_TEMPLATE_SECTION] || {};
  const { section, successCallback } = data || {};
  const [submitting, setSubmitting] = useState(false);

  const { saveSectionAction } = useTemplateBuilder();

  // This is used to close the dialog.
  const handleClose = useCallback(() => {
    dispatch(
      setDialog({ open: false, dialogId: DIALOGS.DELETE_TEMPLATE_SECTION }),
    );
  }, [dispatch]);

  // This is the handler to manage deletion of a template section.
  const deleteHandler = async () => {
    setSubmitting(true);
    const response = await saveSectionAction(section?.id, section?.title, {
      isStarred: false,
    });

    if (response) {
      successCallback?.(section);
      handleClose();
    }
    setSubmitting(false);
  };

  // This is used to render the dialog.
  return (
    <CustomDialog
      hideActions
      dialogProps={{
        open,
        maxWidth: false,
        onClose: handleClose,
        sx: {
          borderRadius: '24px',
        },
      }}
    >
      <>
        <DialogHeader>
          <Typography className="title">Delete Saved Section</Typography>
          <IconButton className={'closeIcon'} onClick={handleClose}>
            <CloseIcon sx={{ fontSize: '13px' }} />
          </IconButton>
        </DialogHeader>

        <DialogContent>
          <Box className="sectionBox">
            <div className="iconBox">
              <SectionIcon className="icon" />
            </div>
            <Stack direction="column">
              <Typography className="sectionText">
                {section?.title || 'Saved Section'}
              </Typography>
              <Typography className="sectionSteps">
                {section?.stepsCount ?? 0} steps
              </Typography>
            </Stack>
          </Box>
          <Typography className="description">
            This action will permanently delete the section from the saved list,
            but it will remain in the linked templates.
          </Typography>
          <Typography
            className="description"
            sx={{ margin: '0px 0px 10px 0px !important' }}
          >
            Are you sure you want to delete this from saved list?
          </Typography>
        </DialogContent>

        <DialogFooter>
          <CustomButton
            variant="text"
            className="cancelBtn"
            onClick={handleClose}
          >
            Cancel
          </CustomButton>

          <CustomButton
            variant="contained"
            className="submitBtn"
            onClick={deleteHandler}
            buttonLoader={submitting}
            disabled={submitting}
            sx={{
              ...(submitting && {
                backgroundColor: 'lightGray !important',
              }),
            }}
          >
            Delete
          </CustomButton>
        </DialogFooter>
      </>
    </CustomDialog>
  );
};

export default DeleteTemplateSection;
